import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	inject,
	Input,
	OnInit,
} from "@angular/core";
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from "@angular/forms";

import { LanguageService } from "@intocare/language";
import { getFormattedAddressLabel } from "@intocare/pipes";

import { AddressComponent, GeolocationResult, LocationResult } from "../../entities/geolocation";
import { addressValidator, countryCodeValidator } from "../../validators";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => LocationInputCountryCodeComponent),
	multi: true,
};

@Component({
	selector: "ic-location-input-country-code",
	templateUrl: "./input-country-code.component.html",
	providers: [INPUT_CONTROL_ACCESSOR],
	styleUrls: ["./input-country-code.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LocationInputCountryCodeComponent implements OnInit, ControlValueAccessor {
	private fb = inject(UntypedFormBuilder);
	private cdRef = inject(ChangeDetectorRef);
	private languageService = inject(LanguageService);

	@Input() type: string;
	@Input() label: string;
	@Input() placeholder = "";
	@Input() icon: string;
	@Input() inputClass: string;
	@Input() help = "";
	@Input() required = true;
	@Input() missing = false;
	@Input() missingText: string;
	@Input() validateCountryCode = true;

	form: UntypedFormGroup;
	address: LocationResult;

	private onTouch = () => {};

	private onChange = (_: unknown) => {};

	updateForm(address: LocationResult) {
		// TODO: The `address.country ??` part can be removed by (https://dev.azure.com/corilusnv/intocare/_workitems/edit/262152)
		const country =
			address.country
			?? new Intl.DisplayNames([this.languageService.language()], { type: "region" }).of(address.countryCode);

		const text = getFormattedAddressLabel({ ...address, country }, "street number{/box}, zip city, country");
		this.form.patchValue({ address, text });

		if (this.form.valid || this.form.hasError("noCountryCode")) {
			return;
		}

		this.address = address;
		this.cdRef.detectChanges();
	}

	writeValue(address: LocationResult) {
		this.resetForm();
		if (address) {
			this.updateForm(address);
		}
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.form.disable();
		} else {
			this.form.enable();
		}
		this.cdRef.detectChanges();
	}

	onBlur() {
		if (this.form.hasError("incompleteAddress")) {
			this.form.patchValue({ text: "" });
			return;
		}
		const text = getFormattedAddressLabel(this.form.value.address, "street number{/box}, zip city, country");
		this.form.patchValue({ text });
	}

	resetForm() {
		this.form.reset();
	}

	clear() {
		this.resetForm();
		this.onChange(null);
		this.onTouch();
	}

	placeChangedCallback(value: GeolocationResult) {
		if (!value.address_components) {
			this.resetForm();
			return;
		}
		const locations = value.address_components;

		const result = {
			street: this.getLongName(locations, "route"),
			number: this.getLongName(locations, "street_number"),
			city: this.getLongName(locations, "locality"),
			state:
				this.getLongName(locations, "administrative_area_level_2")
				|| this.getLongName(locations, "administrative_area_level_1"),
			country: this.getLongName(locations, "country"),
			zip: this.getLongName(locations, "postal_code"),
			countryCode: this.getShortName(locations, "country"),
		};

		this.updateForm(result);
		if (this.form.valid) {
			this.onChange(this.form.getRawValue().address);
			this.onTouch();
		}
	}

	private getLongName(locations: AddressComponent[], search: string) {
		const value = locations.find((location) => location.types.includes(search));
		return value ? value.long_name : "";
	}

	private getShortName(locations: AddressComponent[], search: string) {
		const value = locations.find((location) => location.types.includes(search));

		return value ? value.short_name : "";
	}

	private initForm() {
		let addressValidators;

		addressValidators = [addressValidator];

		if (this.validateCountryCode) {
			addressValidators = [addressValidator, countryCodeValidator];
		}

		this.form = this.fb.group(
			{
				address: [null, [Validators.required]],
				text: [""],
			},
			{ validators: addressValidators },
		);
	}

	ngOnInit() {
		this.initForm();
	}
}
